import { TableRow, TableCell, IconButton, Button, Box } from "@mui/material";
import { Flag } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { format, parseISO, min, max } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { WorkLog, TimeOff } from "../types";
import { StaffWorkLogsContext } from "../StaffWorkLogsDetail";
import { useContext, cloneElement } from "react";
import { DayEntries } from "./DayEntries";
import { pt } from "date-fns/locale";
import { calculateDayStats, calculateHours, getHolidayCreditedHours, resolveClinicId } from "../utils/calculations";
import { getTimeOffConfig, TimeOffType } from "../../../utils/timeOffUtils";
import { CLINICS, TIMEZONE } from "../../../utils/constants";
import { formatDisplayHours, convertToHours } from "../utils/general";

// Time-off types that count toward considered hours: vacation at its daily equivalent, sick/absence at
// their actual entered hours (day_off / public_holiday are markers only and credit nothing).
const CREDITED_TIME_OFF_TYPES = ["holiday", "sick_leave", "missed_justified", "missed_not_justified"];

const getCreditedTimeOff = (timeOffs: Array<TimeOff>, weeklyWorkHoursSchedule: Array<any>) => {
    const creditedEntries = timeOffs.filter((entry) => CREDITED_TIME_OFF_TYPES.includes(entry.type));
    const creditedHours = creditedEntries.reduce((sum, entry) => {
        if (entry.type === "holiday") {
            return sum + getHolidayCreditedHours(entry, weeklyWorkHoursSchedule);
        }
        return sum + convertToHours(calculateHours(entry.start_date, entry.end_date));
    }, 0);

    const times = creditedEntries.length > 0
        ? {
            start: format(utcToZonedTime(min(creditedEntries.map((entry) => parseISO(entry.start_date))), TIMEZONE), "HH:mm"),
            end: format(utcToZonedTime(max(creditedEntries.map((entry) => parseISO(entry.end_date))), TIMEZONE), "HH:mm"),
        }
        : null;

    return { creditedHours, times };
};

type DaySummaryProps = {
    date: Date;
    expandedRows: { [key: string]: boolean };
    toggleRowExpansion: (date: string) => void;
    dateString: string;
    organizedData: { [key: string]: { workLogs: WorkLog[]; timeOffs: TimeOff[] } };
};

export const DaySummary = ({ date, expandedRows, toggleRowExpansion, dateString, organizedData }: DaySummaryProps) => {
    const workLogs = organizedData[dateString].workLogs;
    const timeOffs = organizedData[dateString].timeOffs;
    const { workLogForm, setIsModalOpen, userId, publicHolidaysSet, staffAgreement } = useContext(StaffWorkLogsContext);

    const isPublicHoliday = publicHolidaysSet.has(dateString);

    const { timeStart, timeEnd, workHours, clientsHours, clientsStart, clientsEnd } = calculateDayStats(workLogs);

    const { creditedHours, times: timeOffTimes } = getCreditedTimeOff(timeOffs, staffAgreement?.staff_weekly_hours ?? []);
    const consideredHours = workHours + creditedHours;
    const displayStart = timeStart || timeOffTimes?.start;
    const displayEnd = timeEnd || timeOffTimes?.end;

    const isExpanded = expandedRows[dateString] || false;

    const firstWorkLogClinicId = workLogs[0]?.clinic_id;

    const handleAddClick = () => {
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        workLogForm.setValue("start_date", new Date(dayStart));
        workLogForm.setValue("end_date", new Date(dayStart));
        workLogForm.setValue("notes", "");
        workLogForm.setValue("type", "schedules");
        workLogForm.setValue("clinic_id", resolveClinicId(firstWorkLogClinicId) ?? 1); // normalise nested clinic; default Porto
        workLogForm.setValue("user", userId);
        setIsModalOpen(true);
    };

    return (
        <>
            <TableRow sx={{ backgroundColor: isPublicHoliday ? "rgba(255, 192, 203, 0.3)" : "inherit" }}>
                <TableCell>
                    <Box display="flex" alignItems="center" gap={4}>
                        <IconButton size="small" onClick={() => toggleRowExpansion(dateString)}>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        {format(date, "EEEE, d MMM", { locale: pt })}
                        {isPublicHoliday && <Flag key={`public-holiday-${dateString}`} color="secondary" />}
                        {timeOffs.length > 0 && getTimeOffIcons(timeOffs)}
                    </Box>
                </TableCell>
                <TimeCell value={displayStart} />
                <TimeCell value={clientsStart?.time} />
                <TimeCell value={displayEnd} />
                <TimeCell value={clientsEnd?.time} />
                <TableCell align="center">{formatDisplayHours(consideredHours)}</TableCell>
                <TableCell align="center">{formatDisplayHours(clientsHours)}</TableCell>
                <TableCell align="center">{getUniqueClinics(workLogs)}</TableCell>
                <TableCell align="center">
                    <Button variant="outlined" size="small" onClick={handleAddClick}>
                        Adicionar
                    </Button>
                </TableCell>
            </TableRow>
            {isExpanded && (
                <TableRow>
                    <TableCell colSpan={9} sx={{ padding: 0 }}>
                        <DayEntries
                            date={date}
                            workLogs={organizedData[dateString].workLogs}
                            timeOffs={organizedData[dateString].timeOffs}
                        />
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

type TimeCellProps = {
    value: string | undefined;
};

const TimeCell = ({ value }: TimeCellProps) => {
    return <TableCell align="center">{value}</TableCell>;
};

const renderTimeOffBadge = (timeOff: TimeOff) => {
    const config = getTimeOffConfig(timeOff.type as TimeOffType);
    if (!config) {
        return null;
    }

    const iconWithColor = cloneElement(config.icon as React.ReactElement, { color: config.colorKey });

    const element = (
        <Box
            key={`${timeOff.type}-${timeOff.id}`}
            display="inline-flex"
            alignItems="center"
            gap={1}
            sx={{ color: `${config.colorKey}.main` }}
        >
            {iconWithColor}
            <span>{config.label}</span>
        </Box>
    );

    return element;
};

const getTimeOffIcons = (timeOffs: Array<TimeOff>) => {
    const items = [];
    const seenTypes = new Set<string>();

    for (const timeOff of timeOffs) {
        if (seenTypes.has(timeOff.type)) {
            continue;
        }
        seenTypes.add(timeOff.type);
        const element = renderTimeOffBadge(timeOff);
        if (element) {
            items.push(element);
        }
    }

    return items;
};

const getUniqueClinics = (workLogs: Array<WorkLog>) => {
    const clinicNames = new Set<string>();
    
    for (const log of workLogs) {
        if (log.clinic_id) {
            const clinicName = CLINICS[log.clinic_id] || log.clinic_id;
            clinicNames.add(clinicName);
        }
    }
    
    const uniqueClinics = Array.from(clinicNames).join(", ");
    return uniqueClinics;
};
